/**
 * Light / dark theme for BR OS.
 *
 * Tailwind is configured with darkMode: ["class"], so everything follows the
 * `dark` class on <html> and the token block in index.css. Choice is stored in
 * localStorage under `br-theme`; with no stored choice we follow the OS setting
 * and keep following it while the tab is open.
 */

const STORAGE_KEY = "br-theme";
const MODES = ["light", "dark", "system"];

function prefersDark() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function getStoredMode() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function resolveMode(mode) {
  return mode === "system" ? (prefersDark() ? "dark" : "light") : mode;
}

function paint(mode) {
  if (typeof document === "undefined") return;
  const isDark = resolveMode(mode) === "dark";
  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
}

export function setThemeMode(mode) {
  const next = MODES.includes(mode) ? mode : "system";
  try {
    if (next === "system") {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    // Private mode or blocked storage: still paint for this session.
  }
  paint(next);
  return next;
}

/** Apply the stored choice and keep following the OS while on `system`. */
export function initTheme() {
  const mode = getStoredMode();
  paint(mode);
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return undefined;
  }
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (getStoredMode() === "system") paint("system");
  };
  if (media.addEventListener) {
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }
  if (media.addListener) {
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }
  return undefined;
}

/** light -> dark -> system -> light */
export function nextMode(mode) {
  const i = MODES.indexOf(MODES.includes(mode) ? mode : "system");
  return MODES[(i + 1) % MODES.length];
}

export default initTheme;
